import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Tooltip,
  useColorModeValue,
  useToast,
  Box,
  Text,
  IconButton
} from "@chakra-ui/react";
import {
  applyMaskFromMetadata,
  hasMask,
  isEmailField,
  isUfField,
  isValidEmail,
  ufSelectOptions
} from "utils/masks";
import {
  metaDaQuestaoTemplate,
  rotuloOcorrenciaSecao,
  TIPO_SECAO_ENDERECO,
  TIPO_SECAO_PROPRIETARIO,
  TIPO_SECAO_RESPONSAVEL_TECNICO
} from "utils/secaoTemplateMeta";
import {
  EnderecoCep,
  fetchEnderecoPorCep
} from "store/hooks/useFetchEnderecoCep";
import {
  DadosLoteFiscal,
  fetchVerificarLoteFiscal,
  GeometriaLote
} from "store/hooks/useVerificarLoteFiscal";
import {
  camposEnderecoLote,
  camposProprietarioLote,
  isSecaoEndereco,
  isSecaoProprietario
} from "utils/preencherLoteFiscal";
import { InputCheckbox } from "components/Atomos/Form/Elements/InputCheckbox";
import { InputFileAnexo } from "components/Atomos/Form/Elements/InputFileAnexo";
import { InputMap } from "components/Atomos/Form/Elements/InputMap";
import { InputNumber } from "components/Atomos/Form/Elements/InputNumber";
import { InputSelect } from "components/Atomos/Form/Elements/InputSelect";
import { InputText } from "components/Atomos/Form/Elements/InputText";
import { InputTextArea } from "components/Atomos/Form/Elements/InputTextArea";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import * as yup from "yup";
import {
  Control,
  Controller,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
  useFormContext
} from "react-hook-form";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { cadastrarSolicitacao } from "store/modules/licenciamentourbano/actions/cadastrarSolicitacao";
import {
  DetalheQuestao,
  DetalheSecao,
  ProtocoloVinculadoConfig
} from "store/modules/licenciamentourbano/actions/obterDetalheTipoRequerimento";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { RiDeleteBin6Line } from "react-icons/ri";
import { desformatarProtocolo } from "utils/protocolo";

// Mapeia o enunciado (normalizado) das questões das seções com CEP para a chave
// correspondente no retorno do CEP (ViaCEP via CIDAX). Só os campos presentes aqui
// são preenchidos/zerados a partir do CEP; os demais (número, quadra, lote, matrícula)
// são manuais e não devem ser tocados. Obs.: a cidade vem como "localidade" na
// resposta do CEP, por isso o enunciado "Cidade" precisa apontar para ela.
const CEP_ENUNCIADO_PARA_CHAVE: Record<string, keyof EnderecoCep> = {
  logradouro: "logradouro",
  bairro: "bairro",
  complemento: "complemento",
  cidade: "localidade",
  localidade: "localidade",
  município: "localidade",
  municipio: "localidade",
  uf: "uf",
  estado: "estado"
};

// Seções template cujo campo CEP dispara o auto-preenchimento do endereço. Todas
// possuem CEP + Logradouro/Bairro/Cidade/UF no catálogo do backend, e o preenchimento
// já é por ocorrência (só toca nas questões da própria seção/repetição).
// Fica em escopo de módulo: dentro do componente o Set seria recriado a cada render.
const SECOES_COM_CEP = new Set([
  TIPO_SECAO_ENDERECO,
  TIPO_SECAO_PROPRIETARIO,
  TIPO_SECAO_RESPONSAVEL_TECNICO
]);

// Contexto de D1 (Lote Fiscal). Fornecido pelo FormRequerimento e consumido pelas
// seções: o mapa dispara `onPontoSelecionado`; as seções Endereço/Proprietário
// reagem ao `nonce` (incrementado a cada identificação) para pré-preencher.
type LoteFiscalCtx = {
  geometria: GeometriaLote;
  buscandoLote: boolean;
  onPontoSelecionado: (coord: { lat: number; lng: number }) => void;
};

const LoteFiscalContext = createContext<LoteFiscalCtx | null>(null);

// D2 — chave reservada do campo fixo de protocolo no react-hook-form. Prefixo "__"
// para não colidir com os ids (GUID) das questões nem virar RespostaSolicitacao.
const CAMPO_PROTOCOLO = "__protocoloVinculado";

const buildQuestaoSchema = (
  q: DetalheQuestao,
  tipoSecaoName?: string
): yup.AnySchema => {
  // Máscara/validação resolvida por (tipoSecao + enunciado), já que o backend
  // não envia metadata na questão.
  const meta = metaDaQuestaoTemplate(tipoSecaoName, q.enunciado);

  // E-mail: validar formato (seções prefixadas de contato).
  if (isEmailField(meta)) {
    const base = yup
      .string()
      .transform((v) => (v == null ? "" : v))
      .test("email", "E-mail inválido", (v) => !v || isValidEmail(v));
    return q.obrigatoria
      ? base.required("O campo é obrigatório")
      : base.notRequired();
  }

  return q.obrigatoria
    ? yup.mixed().test("is-valid-type", "O campo é obrigatório", (value) => {
        if (typeof value === "string" && value !== "") return true;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "object") return value !== null;
        return false;
      })
    : yup.mixed().nullable();
};

const generateValidationSchema = (secoes: DetalheSecao[]) => {
  const schemaShape: { [key: string]: yup.AnySchema } = {};

  secoes.forEach((s) => {
    const tipoSecaoName = s.tipoSecao;
    if (s.repetivel) {
      const itemShape = s.questoes.reduce((shape, q) => {
        shape[q.id] = buildQuestaoSchema(q, tipoSecaoName);
        return shape;
      }, {} as { [key: string]: yup.AnySchema });

      schemaShape[s.id] = yup.array().of(yup.object().shape(itemShape));
    } else {
      s.questoes.forEach((q) => {
        schemaShape[q.id] = buildQuestaoSchema(q, tipoSecaoName);
      });
    }
  });

  return yup.object().shape(schemaShape);
};

export const FormRequerimento = ({
  idTipoRequerimento,
  secoes
}: {
  idTipoRequerimento: string;
  secoes: DetalheSecao[];
}) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const toast = useToast();

  const { successPayload: consultaPrevia } = useTypedSelector(
    (state) => state.consultaprevia.obterPlanoDiretorNoPonto
  );

  // D2 — config do campo fixo de protocolo (só existe quando o backend a expõe
  // no detalhe do tipo).
  const { successPayload: detalheTipo } = useTypedSelector(
    (state) => state.licenciamentourbano.obterDetalheTipoRequerimento
  );
  const protocoloConfig = detalheTipo?.protocoloVinculado;

  const baseSchema = generateValidationSchema(secoes);
  // O campo entra no schema sempre que está habilitado — mesmo opcional — só
  // para o cast do yup aplicar o .trim(). Quando ele só entrava no caso
  // obrigatório, o valor do caso habilitado+opcional chegava ao submit com os
  // espaços que o cidadão colou junto, e o backend procura o requerimento
  // vinculado por igualdade EXATA do protocolo.
  //
  // De propósito, nenhuma restrição de FORMATO aqui: este campo recebe o
  // protocolo de um requerimento ANTERIOR, que tanto pode ser o legado
  // alfanumérico (ALV2026000001) quanto o novo, puramente numérico de 14
  // dígitos. Qualquer .matches()/.length()/.min() rejeitaria um dos dois.
  const schema = protocoloConfig?.habilitado
    ? baseSchema.concat(
        yup.object({
          [CAMPO_PROTOCOLO]: protocoloConfig?.obrigatorio
            ? yup
                .string()
                .trim()
                .required(
                  "Informe o protocolo do requerimento vinculado para continuar."
                )
            : yup.string().trim()
        })
      )
    : baseSchema;

  const { isRequesting, errors: errosCadastro } = useTypedSelector(
    (state) => state.licenciamentourbano.cadastrarSolicitacao
  );

  // O slice guarda os erros do cadastro, mas ninguém os lia: a falha ficava
  // muda (o botão só reabilitava) e o cidadão reenviava sem saber o motivo.
  // O formulário fica intacto — só avisa.
  useEffect(() => {
    if (errosCadastro.length === 0) return;
    toast({
      title: "Não foi possível enviar a solicitação",
      description: errosCadastro.join(" "),
      status: "error",
      duration: 6000,
      isClosable: true
    });
  }, [errosCadastro]);

  const hasMapQuestion = secoes.some((s) =>
    s.questoes.some((q) => q.tipoQuestao === "map")
  );

  // O ponto só conta como escolhido depois que a consulta prévia respondeu:
  // é ela que confirma que a coordenada foi de fato submetida ao plano diretor.
  const hasSelectedLocation =
    consultaPrevia !== null && consultaPrevia !== undefined;

  const methods = useForm<{ [key: string]: any }>({
    resolver: yupResolver(schema),
    mode: "onChange"
  });

  // --- D1: Lote Fiscal ---------------------------------------------------------
  // `idLoteFiscalRef` é o vínculo enviado no submit; `geometria` destaca o lote no
  // mapa. O pré-preenchimento é feito TODO aqui (form-level) via `methods.reset`,
  // que é a forma confiável de ressincronizar as seções repetíveis (useFieldArray)
  // no react-hook-form 7.26 (não há `replace`). Uma seção Proprietário repetível
  // recebe 1 ocorrência por contribuinte do lote.
  const idLoteFiscalRef = useRef<string | null>(null);
  const [geometria, setGeometria] = useState<GeometriaLote>(null);
  const [buscandoLote, setBuscandoLote] = useState(false);

  const preencherComLote = useCallback(
    (dados: DadosLoteFiscal): number => {
      const atual = methods.getValues();
      const novos: Record<string, any> = { ...atual };
      // Conta quantos campos REALMENTE receberam valor do lote (para não anunciar
      // "pré-preenchido" quando o lote não tem endereço/contribuinte cadastrado).
      let camposPreenchidos = 0;

      secoes.forEach((s) => {
        if (isSecaoEndereco(s.tipoSecao, s.nome)) {
          const campos = camposEnderecoLote(s.questoes, dados.endereco);
          camposPreenchidos += Object.keys(campos).length;
          if (s.repetivel) {
            novos[s.id] = [campos];
          } else {
            Object.assign(novos, campos);
          }
        } else if (isSecaoProprietario(s.tipoSecao, s.nome)) {
          const ocorrencias = dados.proprietarios.map((p) =>
            camposProprietarioLote(s.questoes, p)
          );
          camposPreenchidos += ocorrencias.reduce(
            (acc, o) => acc + Object.keys(o).length,
            0
          );
          if (s.repetivel) {
            // Ao menos uma ocorrência para não deixar a seção repetível vazia.
            novos[s.id] = ocorrencias.length ? ocorrencias : [{}];
          } else if (ocorrencias[0]) {
            Object.assign(novos, ocorrencias[0]);
          }
        }
      });

      // reset ressincroniza os useFieldArray das seções repetíveis; keepDefaultValues
      // preserva o baseline de "sujo" e não perde os demais campos (map, manuais).
      methods.reset(novos, { keepDefaultValues: true });
      return camposPreenchidos;
    },
    [secoes, methods]
  );

  const onPontoSelecionado = useCallback(
    async (coord: { lat: number; lng: number }) => {
      setBuscandoLote(true);
      try {
        const dados = await fetchVerificarLoteFiscal(coord.lat, coord.lng);
        if (dados?.loteFiscalIdentificado) {
          idLoteFiscalRef.current = dados.idLoteFiscal ?? null;
          setGeometria(dados.geometriaLote ?? null);
          const preenchidos = preencherComLote(dados);
          toast(
            preenchidos > 0
              ? {
                  title: "Lote fiscal identificado",
                  description:
                    "Os dados fiscais disponíveis foram usados para pré-preencher o formulário. Revise antes de enviar.",
                  status: "info",
                  duration: 4000,
                  isClosable: true
                }
              : {
                  title: "Lote fiscal identificado",
                  description:
                    "O lote foi vinculado, mas não há dados cadastrais (endereço/proprietário) para pré-preencher. Preencha manualmente.",
                  status: "info",
                  duration: 5000,
                  isClosable: true
                }
          );
        } else {
          // Sem lote: mantém coordenadas, remove vínculo/geometria; não limpa
          // os campos já preenchidos manualmente pelo cidadão.
          idLoteFiscalRef.current = null;
          setGeometria(null);
        }
      } catch {
        toast({
          title: "Não foi possível verificar o lote fiscal",
          status: "error",
          duration: 2500,
          isClosable: true
        });
      } finally {
        setBuscandoLote(false);
      }
    },
    [preencherComLote, toast]
  );

  const loteFiscalCtx: LoteFiscalCtx = {
    geometria,
    buscandoLote,
    onPontoSelecionado
  };

  // Também trava enquanto a identificação do lote está em voo: concluir no meio
  // da busca envia a solicitação sem o vínculo do lote e sem o pré-preenchimento.
  const isSubmitDisabled =
    (hasMapQuestion && !hasSelectedLocation) || buscandoLote;

  const tooltipMessage = useMemo(() => {
    if (buscandoLote) return "Identificando o lote fiscal, aguarde...";
    if (!hasMapQuestion) return "";
    if (!hasSelectedLocation) {
      return "Selecione um local no mapa que possua instrumentos cadastrados no zoneamento";
    }
    // if (!hasInstrumentos) {
    //   return "Este zoneamento não tem normativos vinculados. Entre em contato com a prefeitura do seu município.";
    // }
    return "";
  }, [hasMapQuestion, hasSelectedLocation, buscandoLote]); // hasInstrumentos

  const onSubmit: SubmitHandler<any> = (data) => {
    const formData = new FormData();

    // Separa o campo fixo de protocolo das respostas das questões.
    const { [CAMPO_PROTOCOLO]: protocoloVinculado, ...respostas } = data;

    formData.append(`TipoRequerimento`, idTipoRequerimento);

    // D1: vincula a solicitação ao lote fiscal identificado (se houver).
    if (idLoteFiscalRef.current) {
      formData.append(`IdLoteFiscal`, idLoteFiscalRef.current);
    }

    // D2: protocolo do requerimento de dependência informado pelo cidadão.
    // O backend localiza esse requerimento por igualdade EXATA do protocolo
    // (sem Trim e sem normalizar caixa), e aqui o cidadão digita à mão um
    // número que recebeu por e-mail — espaço colado junto e caixa trocada são o
    // caso comum. O prefixo do formato legado é sempre maiúsculo, e para o
    // formato novo (só dígitos) o toUpperCase não tem efeito. Continuamos
    // tratando o protocolo como texto: nunca montado nem corrigido no frontend.
    // O hífen do formato novo é só apresentação: quem copia "8K3M-0626" da
    // tela e cola aqui precisa casar com "8K3M0626", que é o valor guardado.
    const protocoloNormalizado = desformatarProtocolo(
      String(protocoloVinculado ?? "")
    ).toUpperCase();
    if (protocoloNormalizado) {
      formData.append(`ProtocoloVinculado`, protocoloNormalizado);
    }

    let index = -1;

    // Em seções repetíveis enviamos IdSecao + IndiceRepeticao EXPLÍCITOS por resposta,
    // para o backend agrupar as ocorrências corretamente (BE-06). Campos vazios podem
    // ser omitidos — o índice explícito garante o agrupamento. Em seções não repetíveis
    // o backend deriva IdSecao (pela questão) e IndiceRepeticao (0).
    const appendValue = (
      questaoId: string,
      value: any,
      ocorrencia?: { idSecao: string; indiceRepeticao: number }
    ) => {
      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);
      if (isEmpty) return;
      index++;

      formData.append(`RespostasSolicitacao[${index}].IdQuestao`, questaoId);
      if (ocorrencia) {
        formData.append(
          `RespostasSolicitacao[${index}].IdSecao`,
          ocorrencia.idSecao
        );
        formData.append(
          `RespostasSolicitacao[${index}].IndiceRepeticao`,
          String(ocorrencia.indiceRepeticao)
        );
      }

      if (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((v) => v instanceof File)
      ) {
        value.forEach((file, fIndex) => {
          formData.append(
            `RespostasSolicitacao[${index}].Files[${fIndex}]`,
            file as Blob
          );
        });
        return;
      }

      if (Array.isArray(value) && value.length > 0) {
        value.forEach((v, vIndex) => {
          formData.append(
            `RespostasSolicitacao[${index}].Values[${vIndex}]`,
            v
          );
        });
        return;
      }

      if (
        value &&
        typeof value === "object" &&
        "lat" in value &&
        "lng" in value
      ) {
        const coordinates = value as { lat: number; lng: number };
        formData.append(
          `RespostasSolicitacao[${index}].Coordinates.Lat`,
          coordinates.lat.toString()
        );
        formData.append(
          `RespostasSolicitacao[${index}].Coordinates.Lng`,
          coordinates.lng.toString()
        );
        return;
      }

      formData.append(`RespostasSolicitacao[${index}].Value`, String(value));
    };

    Object.entries(respostas).forEach(([key, value]) => {
      // Seção repetível: value é um array de ocorrências { [questaoId]: valor }.
      // key === id da seção; o índice do array === índice da ocorrência.
      if (
        Array.isArray(value) &&
        value.every((v) => typeof v === "object" && !(v instanceof File))
      ) {
        (value as Record<string, any>[]).forEach((instance, instanceIndex) => {
          Object.entries(instance).forEach(([questaoId, questaoValue]) => {
            appendValue(questaoId, questaoValue, {
              idSecao: key,
              indiceRepeticao: instanceIndex
            });
          });
        });
        return;
      }

      // Campo normal (seção não repetível)
      appendValue(key, value);
    });

    if (Array.from(formData.entries()).length > 0) {
      dispatch(
        cadastrarSolicitacao({
          respostas: formData,
          // Abre o detalhe da solicitação criada: é lá que o protocolo aparece
          // (o cadastro devolve só o id). O flag pede o alerta de confirmação.
          onSuccess: (idSolicitacao) => {
            if (!idSolicitacao) {
              toast({
                title: "Solicitação realizada com sucesso",
                status: "success",
                duration: 2000,
                isClosable: true
              });
              navigate("/");
              return;
            }
            navigate(`/licenciamento-urbano/requerimento/${idSolicitacao}`, {
              state: { recemCriada: true }
            });
          }
        })
      );
    }
  };

  return (
    <LoteFiscalContext.Provider value={loteFiscalCtx}>
      <FormProvider {...methods}>
        <Flex
          as="form"
          onSubmit={methods.handleSubmit(onSubmit)}
          w="full"
          direction="column"
          gap={5}
        >
          {/* D2 — Campo fixo de protocolo: sempre no TOPO, fora das seções. */}
          {protocoloConfig?.habilitado && (
            <ProtocoloVinculadoField config={protocoloConfig} />
          )}

          <Accordion
            allowMultiple
            defaultIndex={secoes.map((_, i) => i)}
            display="flex"
            flexDirection="column"
            gap={4}
          >
            {secoes.map((s) => (
              <SecaoAccordionItem key={s.id} secao={s} />
            ))}
          </Accordion>

          <HStack w="full" justifyContent="flex-end" mt={5}>
            <Button variant="outline" mr={3} onClick={() => navigate("/")}>
              Cancelar
            </Button>
            <Tooltip
              label={tooltipMessage}
              isDisabled={!isSubmitDisabled}
              hasArrow
            >
              <Box>
                <Button
                  colorScheme="main"
                  type="submit"
                  isDisabled={isSubmitDisabled}
                  isLoading={isRequesting}
                >
                  Concluir solicitação
                </Button>
              </Box>
            </Tooltip>
          </HStack>
        </Flex>
      </FormProvider>
    </LoteFiscalContext.Provider>
  );
};

// Cada seção do formulário vira um item de accordion (expandir/recolher
// individual + resumo quando recolhido). O conteúdo do painel fica SEMPRE
// montado (só escondido quando recolhido) para não perder o estado do
// react-hook-form / useFieldArray das ocorrências repetíveis.
function SecaoAccordionItem({ secao }: { secao: DetalheSecao }) {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const qtd = secao.questoes.length;
  const resumo = `${secao.repetivel ? "Repetível • " : ""}${qtd} ${
    qtd === 1 ? "campo" : "campos"
  }`;

  return (
    <AccordionItem
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      overflow="hidden"
    >
      {({ isExpanded }) => (
        <>
          <AccordionButton py={3} px={4}>
            <Box flex={1} minW={0} textAlign="left">
              <HStack spacing={2}>
                <Text fontSize="lg" fontWeight="semibold">
                  {secao.nome}
                </Text>
                {secao.repetivel && (
                  <Badge colorScheme="purple">Repetível</Badge>
                )}
              </HStack>
              {!isExpanded && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {resumo}
                </Text>
              )}
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={4} pt={0} pb={4}>
            {secao.repetivel ? (
              <RepeatableSection secao={secao} />
            ) : (
              <FormFields
                questoes={secao.questoes}
                tipoSecaoName={secao.tipoSecao}
              />
            )}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
}

function RepeatableSection({ secao }: { secao: DetalheSecao }) {
  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: secao.id
  });

  // Garante que sempre existe uma instância inicial
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && fields.length === 0) {
      append({}, { shouldFocus: false });
    }
    initialized.current = true;
  }, []);

  return (
    <Flex direction="column" gap={3}>
      {fields.map((field, instanceIndex) => (
        <Flex
          key={field.id}
          direction="column"
          gap={3}
          borderLeft="3px solid"
          borderLeftColor="main.500"
          pl={6}
        >
          <HStack justifyContent="space-between">
            <Text fontSize="sm" fontWeight="medium" color="gray.500">
              {rotuloOcorrenciaSecao(
                secao.tipoSecao,
                secao.nome,
                instanceIndex + 1
              )}
            </Text>
            {/* A primeira ocorrência nunca pode ser excluída (apenas as adicionais). */}
            {instanceIndex > 0 && (
              <IconButton
                aria-label="Remover"
                icon={<RiDeleteBin6Line />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => remove(instanceIndex)}
              />
            )}
          </HStack>

          <FormFields
            questoes={secao.questoes}
            namePrefix={`${secao.id}.${instanceIndex}.`}
            tipoSecaoName={secao.tipoSecao}
          />
        </Flex>
      ))}
      <Button
        size="sm"
        colorScheme="main"
        variant="outline"
        onClick={() => append({})}
      >
        + Adicionar {secao.nome}
      </Button>
    </Flex>
  );
}

export const FormFields = ({
  questoes,
  namePrefix = "",
  tipoSecaoName
}: {
  questoes: DetalheQuestao[];
  namePrefix?: string;
  tipoSecaoName?: string;
}) => {
  const {
    control,
    register,
    setValue,
    formState: { errors }
  } = useFormContext();
  const toast = useToast();
  const [buscandoCep, setBuscandoCep] = useState(false);
  const lote = useContext(LoteFiscalContext);

  const isEndereco = tipoSecaoName === TIPO_SECAO_ENDERECO;
  const suportaCep = !!tipoSecaoName && SECOES_COM_CEP.has(tipoSecaoName);

  // Preenche as demais questões da seção a partir do CEP consultado, casando cada
  // questão pela chave (enunciado normalizado ~ resposta do backend).
  const preencherEndereco = useCallback(
    (endereco: EnderecoCep) => {
      questoes.forEach((q) => {
        const chave = q.enunciado.trim().toLowerCase();
        if (chave === "cep") return; // não sobrescreve o CEP digitado

        // Só mexe nos campos que vêm do CEP; os demais (número, quadra, lote,
        // matrícula) são preenchidos à mão e NÃO devem ser limpos ao trocar de CEP.
        const enderecoKey = CEP_ENUNCIADO_PARA_CHAVE[chave];
        if (!enderecoKey) return;

        const valor = endereco[enderecoKey] ?? "";

        // Fora da seção Endereço o complemento é o do endereço da PESSOA (digitado
        // à mão): limpar seria destrutivo, então só preenchemos se o CEP devolver algo.
        if (!valor && enderecoKey === "complemento" && !isEndereco) return;

        // Sobrescreve com o novo endereço e zera o campo quando o CEP não retorna
        // aquele dado (ex.: complemento vazio), evitando resíduo do CEP anterior.
        setValue(`${namePrefix}${q.id}`, valor, {
          shouldValidate: true,
          shouldDirty: true
        });
      });
    },
    [questoes, namePrefix, setValue, isEndereco]
  );

  const handleCepBlur = useCallback(
    async (cep: string) => {
      const cepDigits = cep.replace(/\D/g, "");
      if (cepDigits.length !== 8) return;
      try {
        setBuscandoCep(true);
        const endereco = await fetchEnderecoPorCep(cepDigits);
        if (endereco) {
          preencherEndereco(endereco);
        } else {
          toast({
            title: "CEP não encontrado",
            status: "warning",
            duration: 2500,
            isClosable: true
          });
        }
      } catch {
        toast({
          title: "Não foi possível buscar o endereço pelo CEP",
          status: "error",
          duration: 2500,
          isClosable: true
        });
      } finally {
        setBuscandoCep(false);
      }
    },
    [preencherEndereco, toast]
  );

  const getError = (questaoId: string) => {
    if (!namePrefix) return errors[questaoId];
    // namePrefix = "secaoId.instanceIndex." → split to navigate errors object
    const parts = namePrefix.replace(/\.$/, "").split(".");
    let node: any = errors;
    for (const part of parts) {
      node = node?.[part];
    }
    return node?.[questaoId];
  };

  const fieldName = (questaoId: string) => `${namePrefix}${questaoId}`;

  const questoesFields = useMemo(() => {
    return questoes.map((q) => {
      const name = fieldName(q.id);
      const error = getError(q.id);
      const meta = metaDaQuestaoTemplate(tipoSecaoName, q.enunciado);
      const isCampoCep =
        suportaCep && q.enunciado.trim().toLowerCase() === "cep";

      // Campos prefixados: UF (select)
      if (isUfField(meta)) {
        return (
          <InputSelect
            key={q.id}
            control={control}
            label={q.enunciado}
            name={name}
            option={ufSelectOptions(meta)}
            error={error}
          />
        );
      }

      // Campos prefixados com máscara (CPF/CNPJ, CEP, telefone). No CEP, o blur
      // dispara o auto-fill do endereço (rota do CIDAX).
      if (hasMask(meta)) {
        return (
          <Controller
            key={q.id}
            name={name}
            control={control}
            render={({ field }) => (
              <FormControl isInvalid={!!error}>
                <FormLabel fontSize="sm">{q.enunciado}</FormLabel>
                <InputGroup>
                  <Input
                    value={field.value ?? ""}
                    inputMode="numeric"
                    onChange={(e) =>
                      field.onChange(
                        applyMaskFromMetadata(meta, e.target.value)
                      )
                    }
                    onBlur={(e) => {
                      field.onBlur();
                      if (isCampoCep) handleCepBlur(e.target.value);
                    }}
                  />
                  {isCampoCep && buscandoCep && (
                    <InputRightElement>
                      <Spinner size="sm" />
                    </InputRightElement>
                  )}
                </InputGroup>
                {error && (
                  <FormErrorMessage>{(error as any).message}</FormErrorMessage>
                )}
              </FormControl>
            )}
          />
        );
      }

      if (q.tipoQuestao === "short_text") {
        return (
          <InputText
            key={q.id}
            label={q.enunciado}
            name={name}
            register={register}
            error={error}
            maxLength={255}
          />
        );
      }
      if (q.tipoQuestao === "large_text") {
        return (
          <InputTextArea
            key={q.id}
            label={q.enunciado}
            name={name}
            register={register}
            error={error}
            maxLength={1000}
          />
        );
      }
      if (q.tipoQuestao === "numeric") {
        return (
          <InputNumber
            key={q.id}
            label={q.enunciado}
            name={name}
            min={0}
            control={control}
            error={error}
          />
        );
      }
      if (q.tipoQuestao === "date") {
        return (
          <InputText
            key={q.id}
            label={q.enunciado}
            name={name}
            type="date"
            register={register}
            error={error}
          />
        );
      }
      if (q.tipoQuestao === "map") {
        return (
          <InputMap
            key={q.id}
            label={q.enunciado}
            name={name}
            control={control}
            error={error}
            onPontoSelecionado={lote?.onPontoSelecionado}
            geometria={lote?.geometria}
            buscandoLote={lote?.buscandoLote}
          />
        );
      }
      if (q.tipoQuestao === "select_list") {
        return (
          <SelectListSuboptions
            key={q.id}
            control={control}
            questao={q}
            namePrefix={namePrefix}
          />
        );
      }
      if (q.tipoQuestao === "document") {
        return (
          <InputFileAnexo
            key={q.id}
            name={name}
            label={q.enunciado}
            error={error}
            accept={q.opcoes.map((o) => o.text)}
          />
        );
      }
      if (q.tipoQuestao === "multi_select") {
        return (
          <InputCheckbox
            key={q.id}
            control={control}
            label={q.enunciado}
            name={name}
            option={[...q.opcoes]
              .sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
              .map((o) => ({ value: o.id, label: o.text }))}
            error={error}
          />
        );
      }

      return null;
    });
  }, [
    questoes,
    errors,
    namePrefix,
    tipoSecaoName,
    suportaCep,
    buscandoCep,
    handleCepBlur,
    lote?.geometria,
    lote?.buscandoLote,
    lote?.onPontoSelecionado
  ]);

  return (
    <Flex direction="column" gap={5}>
      {questoesFields}
    </Flex>
  );
};

// D2 — Campo fixo de protocolo renderizado no topo do formulário do cidadão.
// Fica FORA das seções (não é arrastável nem reordenável) e usa a chave reservada
// CAMPO_PROTOCOLO no react-hook-form.
function ProtocoloVinculadoField({
  config
}: {
  config: ProtocoloVinculadoConfig;
}) {
  const {
    register,
    formState: { errors }
  } = useFormContext();
  const bg = useColorModeValue("main.50", "gray.700");
  const borderColor = useColorModeValue("main.200", "main.600");
  const error = errors[CAMPO_PROTOCOLO] as any;

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      bg={bg}
      borderRadius="md"
      p={4}
    >
      <HStack mb={1} spacing={2}>
        <Text fontSize="md" fontWeight="semibold">
          {config.rotulo || "Protocolo do requerimento vinculado"}
        </Text>
        {config.obrigatorio ? (
          <Badge colorScheme="red">Obrigatório</Badge>
        ) : (
          <Badge colorScheme="gray">Opcional</Badge>
        )}
      </HStack>
      {config.orientacao && (
        <Text fontSize="sm" color="gray.600" mb={1}>
          {config.orientacao}
        </Text>
      )}
      {config.nomeTipoRequerimentoVinculado && (
        <Text fontSize="xs" color="gray.500" mb={2}>
          Tipo esperado: {config.nomeTipoRequerimentoVinculado}
        </Text>
      )}
      {/*
        Input deliberadamente CRU: sem máscara, sem maxLength, sem pattern e sem
        inputMode="numeric". Este campo recebe o protocolo de um requerimento
        ANTERIOR, e TRÊS formatos convivem na base: o legado alfanumérico de 13
        caracteres (ALV2026000001), o numérico de 14 dígitos (26090845296001) e
        o novo de 8 (8K3M0626, exibido como 8K3M-0626). Nenhuma restrição
        client-side aceita os três, e inputMode="numeric" ainda esconderia o
        teclado alfabético no mobile. O hífen colado junto é removido no envio
        (desformatarProtocolo); o resto o backend valida.
      */}
      <FormControl isInvalid={!!error}>
        <Input
          bg="white"
          placeholder="Informe o protocolo do requerimento anterior"
          {...register(CAMPO_PROTOCOLO)}
        />
        {error && <FormErrorMessage>{error.message}</FormErrorMessage>}
      </FormControl>
    </Box>
  );
}

function SelectListSuboptions({
  control,
  questao,
  namePrefix = ""
}: {
  control: Control<any, object>;
  questao: DetalheQuestao;
  namePrefix?: string;
}) {
  const {
    watch,
    register,
    formState: { errors }
  } = useFormContext();

  const name = `${namePrefix}${questao.id}`;
  const opcaoSelecionada = watch(name);
  const opcao = questao.opcoes.find((o) => o.id === opcaoSelecionada);

  const subName = `${namePrefix}${opcao?.id ?? ""}`;

  // Em seção repetível o erro fica aninhado (secaoId.idx.questaoId); navega o caminho.
  const parts = namePrefix.replace(/\.$/, "").split(".").filter(Boolean);
  const fieldError = parts.reduce((acc: any, p) => acc?.[p], errors)?.[
    questao.id
  ];

  return (
    <>
      <InputSelect
        control={control}
        label={questao.enunciado}
        name={name}
        option={[...questao.opcoes]
          .sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
          .map((o) => ({ value: o.id, label: o.text }))}
        error={fieldError}
      />

      {opcao?.opcoes &&
        opcao.opcoes.length > 0 &&
        opcao.tipoOpcao == "item_list" && (
          <InputSelect
            control={control}
            label={opcao.text}
            name={subName}
            option={[...opcao.opcoes]
              .sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
              .map((o) => ({ value: o.id, label: o.text }))}
          />
        )}

      {opcao?.opcoes && opcao.tipoOpcao == "text_list" && (
        <InputText label={opcao.text} name={subName} register={register} />
      )}
    </>
  );
}
